// ─── Fuentes oficiales por CCAA para el informe SyB 7 ───────────────────────
// Mapeo de comunidad autónoma → conjuntos de fuentes oficiales que aplican
// a los tres bloques del informe (sismicidad · incendios · geotecnia).
//
// La sismicidad es siempre nacional (NCSE-02, IGN) y no depende de la CCAA.
// Los bloques de incendios y geotecnia sí dependen del territorio: cada CCAA
// tiene su autoridad competente y sus datasets oficiales propios.
//
// Este archivo debe estar sincronizado con el manual BREEAM ES SyB 7, que
// exige documentación oficial del emplazamiento (no basta con fuentes
// nacionales genéricas cuando la CCAA publica su propio inventario de riesgo).

// ── Mapping provincia → CCAA (INE oficial 2024) ──────────────────────────
const PROV_TO_CCAA = {
  'ALMERIA': 'ANDALUCÍA', 'CADIZ': 'ANDALUCÍA', 'CORDOBA': 'ANDALUCÍA',
  'GRANADA': 'ANDALUCÍA', 'HUELVA': 'ANDALUCÍA', 'JAEN': 'ANDALUCÍA',
  'MALAGA': 'ANDALUCÍA', 'SEVILLA': 'ANDALUCÍA',
  'HUESCA': 'ARAGÓN', 'TERUEL': 'ARAGÓN', 'ZARAGOZA': 'ARAGÓN',
  'ASTURIAS': 'ASTURIAS', 'PRINCIPADO DE ASTURIAS': 'ASTURIAS',
  'BALEARES': 'ISLAS BALEARES', 'ILLES BALEARS': 'ISLAS BALEARES',
  'LAS PALMAS': 'CANARIAS', 'SANTA CRUZ DE TENERIFE': 'CANARIAS',
  'CANTABRIA': 'CANTABRIA',
  'ALBACETE': 'CASTILLA-LA MANCHA', 'CIUDAD REAL': 'CASTILLA-LA MANCHA',
  'CUENCA': 'CASTILLA-LA MANCHA', 'GUADALAJARA': 'CASTILLA-LA MANCHA',
  'TOLEDO': 'CASTILLA-LA MANCHA',
  'AVILA': 'CASTILLA Y LEÓN', 'BURGOS': 'CASTILLA Y LEÓN',
  'LEON': 'CASTILLA Y LEÓN', 'PALENCIA': 'CASTILLA Y LEÓN',
  'SALAMANCA': 'CASTILLA Y LEÓN', 'SEGOVIA': 'CASTILLA Y LEÓN',
  'SORIA': 'CASTILLA Y LEÓN', 'VALLADOLID': 'CASTILLA Y LEÓN',
  'ZAMORA': 'CASTILLA Y LEÓN',
  'BARCELONA': 'CATALUÑA', 'GIRONA': 'CATALUÑA',
  'LLEIDA': 'CATALUÑA', 'TARRAGONA': 'CATALUÑA',
  'ALICANTE': 'COMUNIDAD VALENCIANA', 'ALACANT': 'COMUNIDAD VALENCIANA',
  'CASTELLON': 'COMUNIDAD VALENCIANA', 'CASTELLO': 'COMUNIDAD VALENCIANA',
  'VALENCIA': 'COMUNIDAD VALENCIANA',
  'BADAJOZ': 'EXTREMADURA', 'CACERES': 'EXTREMADURA',
  'A CORUNA': 'GALICIA', 'LA CORUNA': 'GALICIA', 'LUGO': 'GALICIA',
  'OURENSE': 'GALICIA', 'PONTEVEDRA': 'GALICIA',
  'MADRID': 'COMUNIDAD DE MADRID',
  'MURCIA': 'REGIÓN DE MURCIA',
  'NAVARRA': 'COMUNIDAD FORAL DE NAVARRA',
  'ALAVA': 'PAÍS VASCO', 'ARABA': 'PAÍS VASCO',
  'GUIPUZCOA': 'PAÍS VASCO', 'GIPUZKOA': 'PAÍS VASCO',
  'VIZCAYA': 'PAÍS VASCO', 'BIZKAIA': 'PAÍS VASCO',
  'LA RIOJA': 'LA RIOJA', 'RIOJA': 'LA RIOJA',
  'CEUTA': 'CEUTA', 'MELILLA': 'MELILLA',
}

function slug(s) {
  if (!s) return ''
  return s.toString()
    .normalize('NFKD').replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ').trim().toUpperCase()
}

export function getCCAA(provincia) {
  return PROV_TO_CCAA[slug(provincia)] || null
}

// ── Fuentes por CCAA · fallback nacional para las no mapeadas ────────────
const NATIONAL_FALLBACK = {
  fire: {
    authority: 'Ministerio para la Transición Ecológica (MITECO) · EGIF',
    dataset: 'Estadística General de Incendios Forestales · Zonas de Alto Riesgo (Ley 43/2003)',
    framework: 'Ley 43/2003 de Montes · art. 48 declaración de Zonas de Alto Riesgo',
    urlName: 'MITECO · Incendios Forestales',
    url: 'https://www.miteco.gob.es/es/biodiversidad/temas/incendios-forestales.html',
    hasGreuField: false,
  },
  geotech: {
    authority: 'Instituto Geológico y Minero de España (IGME)',
    dataset: 'Base de datos MOVES · movimientos del terreno',
    framework: 'IGME · Servicio de cartografía geocientífica',
    urlName: 'IGME · Movimientos del terreno',
    url: 'https://info.igme.es/BDMoves/',
  },
}

const CCAA_SOURCES = {
  'CATALUÑA': {
    fire: {
      authority: 'Generalitat de Catalunya · Direcció General de Protecció Civil',
      dataset: 'INFOCAT · zonas de risc greu d\'incendis',
      framework: 'Decret 64/1995 · Pla ALFA · Ley 43/2003 de Montes',
      wms: 'pcivil.icgc.cat/ogc/geoservei',
      layers: 'infocat_perill · infocat_vulnerabilitat · zones_risc_greu_incendis',
      urlName: 'INFOCAT · Protecció Civil',
      url: 'https://interior.gencat.cat/ca/arees_dactuacio/proteccio-civil/plans-de-proteccio-civil/plans-especials/infocat/',
      hasGreuField: true,  // el campo booleano "zona de risc greu" es específico catalán
    },
    geotech: {
      authority: 'Institut Cartogràfic i Geològic de Catalunya (ICGC) + IGME',
      dataset: 'ICGC · Inestabilitats + IGME · MOVES',
      framework: 'ICGC · Servei geològic de Catalunya',
      urlName: 'ICGC · Cartografia',
      url: 'https://www.icgc.cat/',
    },
  },

  'CANARIAS': {
    fire: {
      authority: 'Gobierno de Canarias · Dirección General de Seguridad y Emergencias + Cabildo Insular',
      dataset: 'PEIFCA · Plan Especial de Protección Civil y Atención de Emergencias por Incendios Forestales de Canarias · ZARI (Zonas de Alto Riesgo de Incendio)',
      framework: 'Decreto 100/2002 · Zonas de Alto Riesgo canarias · Ley 43/2003 de Montes',
      wms: 'idecan2.grafcan.es/ServicioWMS/ZARI',    // WMS público de IDECanarias
      layers: 'ZARI',                                 // Capa Zonas de Alto Riesgo de Incendios
      urlName: 'IDECanarias · ZARI',
      url: 'https://www.idecanarias.es/listado_servicios/zonas-alto-riesgo-incendios-forestales-zari',
      urlCabildo: 'https://www.tenerife.es/planes/PIEF/',
      hasGreuField: false,
      hasVolcanicRisk: true,  // Canarias es la única CCAA con riesgo volcánico
    },
    geotech: {
      authority: 'IGME + INVOLCAN (Instituto Volcanológico de Canarias) + PEVOLCA',
      dataset: 'IGME · MOVES + INVOLCAN · red de vigilancia volcánica + PEVOLCA (RD 407/1992)',
      framework: 'PEVOLCA · Plan Especial de Protección Civil por Riesgo Volcánico',
      urlName: 'INVOLCAN · Vigilancia volcánica',
      url: 'https://www.involcan.org/',
      hasVolcanicRisk: true,
      wmsVolcanologia: 'wms-volcanologia.ign.es/volcanologia',  // Capa 'erupciones' históricas
      volcanicLayer: 'erupciones',
    },
    weather: {
      // AEMET no publica WMS público de avisos, solo CAP/RSS. Enlazamos al panel
      // oficial de Meteoalerta filtrado para Canarias.
      authority: 'AEMET · Sistema Nacional de Predicción · Meteoalerta',
      dataset: 'Avisos de fenómenos meteorológicos adversos (viento, lluvia, calima)',
      url: 'https://www.aemet.es/es/eltiempo/prediccion/avisos?w=hoy&a=can',
      urlName: 'AEMET Meteoalerta Canarias',
    },
  },

  'ANDALUCÍA': {
    fire: {
      authority: 'Junta de Andalucía · Consejería de Sostenibilidad + Plan INFOCA',
      dataset: 'INFOCA · Plan de Emergencia por Incendios Forestales de Andalucía',
      framework: 'Decreto 371/2010 · Plan INFOCA · Ley 43/2003 de Montes',
      urlName: 'Plan INFOCA',
      url: 'https://www.juntadeandalucia.es/medioambiente/portal/areas-tematicas/incendios-forestales',
      hasGreuField: false,
    },
    geotech: NATIONAL_FALLBACK.geotech,
  },

  'COMUNIDAD VALENCIANA': {
    fire: {
      authority: 'Generalitat Valenciana · Agencia Valenciana de Seguridad y Respuesta a las Emergencias',
      dataset: 'PATFOR · Plan de Acción Territorial Forestal + Plan Especial frente al Riesgo de Incendios Forestales',
      framework: 'Decreto 163/1998 · Plan Especial de Incendios Forestales · Ley 43/2003 de Montes',
      urlName: 'Generalitat Valenciana · Incendios',
      url: 'https://agroambient.gva.es/es/web/prevencion-de-incendios/planes-de-prevencion',
      hasGreuField: false,
    },
    geotech: NATIONAL_FALLBACK.geotech,
  },

  'GALICIA': {
    fire: {
      authority: 'Xunta de Galicia · Consellería do Medio Rural',
      dataset: 'PLADIGA · Plan de Prevención e Defensa contra os Incendios Forestais de Galicia',
      framework: 'Ley 3/2007 · Prevención e defensa contra os incendios forestais de Galicia',
      urlName: 'PLADIGA Galicia',
      url: 'https://mediorural.xunta.gal/es/temas/forestal/incendios-forestales/pladiga',
      hasGreuField: false,
    },
    geotech: NATIONAL_FALLBACK.geotech,
  },

  'CASTILLA-LA MANCHA': {
    fire: {
      authority: 'Junta de Comunidades de Castilla-La Mancha · Consejería de Desarrollo Sostenible',
      dataset: 'INFOCAM · Plan Director de Defensa contra Incendios Forestales',
      framework: 'Decreto 195/2020 · INFOCAM · Ley 3/2008 de montes y gestión forestal',
      urlName: 'INFOCAM',
      url: 'https://www.castillalamancha.es/gobierno/desarrollosostenible/estructura/dgpaymn/actuaciones/plan-director-de-defensa-contra-incendios-forestales-de-castilla-la-mancha-infocam',
      hasGreuField: false,
    },
    geotech: NATIONAL_FALLBACK.geotech,
  },
}

// ── Función principal ──────────────────────────────────────────────────
export function getSyb7Sources(provincia, ccaa) {
  // Prioridad: si el usuario ya tiene la CCAA explícita, buscar match
  // tolerante a mayúsculas / acentos / eñes en las claves del diccionario.
  // Si no, derivar de la provincia (getCCAA ya devuelve la clave con
  // acentos correctos). Como último recurso, fallback nacional.
  //
  // OJO: usar slug() como clave rompe el matching porque nuestras claves
  // tienen Ñ y acentos ('CATALUÑA', 'ANDALUCÍA', 'ARAGÓN', 'CASTILLA Y
  // LEÓN'...) y slug() los elimina, dejando 'CATALUNA', 'ANDALUCIA', etc.
  let ccaaKey = null
  if (ccaa) {
    const inputSlug = slug(ccaa)
    ccaaKey = Object.keys(CCAA_SOURCES).find(k => slug(k) === inputSlug) || null
  }
  if (!ccaaKey) {
    ccaaKey = getCCAA(provincia)
  }
  const found = ccaaKey ? CCAA_SOURCES[ccaaKey] : null
  if (found) {
    return { ccaa: ccaaKey, fire: found.fire, geotech: found.geotech, isNationalFallback: false }
  }
  return {
    ccaa: ccaaKey || 'DESCONOCIDA',
    fire: NATIONAL_FALLBACK.fire,
    geotech: NATIONAL_FALLBACK.geotech,
    isNationalFallback: true,
  }
}
